import { Suspense, lazy, useEffect } from "react";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { trackPageView } from "./lib/analytics";

const Home = lazy(() => import("./pages/Home"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const ServiceRequest = lazy(() => import("./pages/ServiceRequest"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogApplianceRepairVsReplace = lazy(() => import("./pages/BlogApplianceRepairVsReplace"));
const BlogPremiumKitchenMaintenance = lazy(() => import("./pages/BlogPremiumKitchenMaintenance"));
const BlogSubZeroRepairChallenges = lazy(() => import("./pages/BlogSubZeroRepairChallenges"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const Contact = lazy(() => import("./pages/Contact"));

function ScrollToTopOnRouteChange() {
  const [location] = useLocation();

  useEffect(() => {
    // Preserve in-page anchor behavior like /#services
    if (location.includes("#")) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location]);

  return null;
}

function GA4PageTracking() {
  const [location] = useLocation();

  useEffect(() => {
    trackPageView(location);
  }, [location]);

  return null;
}

function NormalizeTrailingSlash() {
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const [pathAndQuery, hash = ""] = location.split("#");
    const [pathname, query = ""] = pathAndQuery.split("?");

    if (pathname.length > 1 && pathname.endsWith("/")) {
      const normalizedPath = pathname.replace(/\/+$/g, "");
      const normalized = `${normalizedPath}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
      setLocation(normalized);
    }
  }, [location, setLocation]);

  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      {/* Service Pages */}
      <Route path="/refrigerator-repair-santa-monica" component={ServicePage} />
      <Route path="/washer-repair-santa-monica" component={ServicePage} />
      <Route path="/dryer-repair-santa-monica" component={ServicePage} />
      <Route path="/oven-repair-santa-monica" component={ServicePage} />
      <Route path="/stovetop-repair-santa-monica" component={ServicePage} />
      <Route path="/dishwasher-repair-santa-monica" component={ServicePage} />
      <Route path="/oven-hood-repair-santa-monica" component={ServicePage} />
      {/* Location Pages */}
      <Route path="/appliance-repair-west-la" component={LocationPage} />
      <Route path="/appliance-repair-brentwood" component={LocationPage} />
      <Route path="/appliance-repair-pacific-palisades" component={LocationPage} />
      <Route path="/appliance-repair-beverly-hills" component={LocationPage} />
      <Route path="/service-request" component={ServiceRequest} />
      <Route path="/service-areas" component={ServiceAreas} />
      <Route path="/blog" component={Blog} />
      <Route path="/blog/appliance-repair-vs-replace-smart-homeowners-guide" component={BlogApplianceRepairVsReplace} />
      <Route path="/blog/expert-tips-for-premium-kitchen-appliance-maintenance" component={BlogPremiumKitchenMaintenance} />
      <Route path="/blog/sub-zero-appliance-repair-unique-challenges-expertise" component={BlogSubZeroRepairChallenges} />
      <Route path="/thank-you" component={ThankYou} />
      <Route path="/contact" component={Contact} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/terms-and-conditions" component={TermsAndConditions} />
      {/* Brand Pages */}
      <Route path="/sub-zero-refrigerator-repair-santa-monica" component={BrandPage} />
      <Route path="/viking-appliance-repair-santa-monica" component={BrandPage} />
      <Route path="/thermador-appliance-repair-santa-monica" component={BrandPage} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <NormalizeTrailingSlash />
        <ScrollToTopOnRouteChange />
        <GA4PageTracking />
        <Suspense fallback={<div className="min-h-screen bg-brand-cream" />}>
          <Router />
        </Suspense>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
